import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  FormControlLabel,
  Checkbox,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import DescriptionIcon from "@mui/icons-material/Description";
import api from "../../utils/api";
import ColorManagement from "./ColorManagement";
import ArchitecturalBillingSetupManager from "./ArchitecturalBillingSetupManager";
import ArchitecturalFormBuilder from "./ArchitecturalFormBuilder";
import ModuleStatuses from "./ModuleStatuses";
import ARStatusWorkflow from "./ARStatusWorkflow";
import ARAdvancedSettings from "./ARAdvancedSettings";
import ARManual from "./ARManual";
import ARCCustomFormBuilder from "./ARCCustomFormBuilder";

const ArchitecturalRequestManager = () => {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [acknowledgments, setAcknowledgments] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ type: "", id: null });
  const [tabIndex, setTabIndex] = useState(0);
  const [formBuilderTab, setFormBuilderTab] = useState(0); // 0=Form Fields, 1=Acknowledgments, 2=Info Sections

  const [subtypeDialog, setSubtypeDialog] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [subtypeForm, setSubtypeForm] = useState({
    subtype_id: null,
    type_id: null,
    subtype_name: "",
    requires_freeform: false,
  });

  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [editingAck, setEditingAck] = useState(null);
  const [ackForm, setAckForm] = useState({
    id: null,
    bullet_text: "",
    sort_order: 0,
    is_active: true,
  });

  // Info Sections state
  const [infoSections, setInfoSections] = useState([]);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [editingInfo, setEditingInfo] = useState(null);
  const COLOR_PRESETS = [
    { label: "Yellow (warning)",  bg: "#fffde7", border: "#ffe082" },
    { label: "Blue (info)",       bg: "#e3f2fd", border: "#90caf9" },
    { label: "Green (success)",   bg: "#f1f8e9", border: "#aed581" },
    { label: "Orange (caution)",  bg: "#fff3e0", border: "#ffcc80" },
    { label: "Purple (notice)",   bg: "#f3e5f5", border: "#ce93d8" },
    { label: "White (neutral)",   bg: "#ffffff", border: "#e0e0e0" },
    { label: "Custom",            bg: "",        border: ""         },
  ];
  const [infoForm, setInfoForm] = useState({
    title: "",
    content: "",
    content_type: "paragraph",
    bg_color: "#fffde7",
    border_color: "#ffe082",
    color_preset: 0,
    sort_order: 0,
    is_active: true,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchTypes();
    fetchSubtypes();
    fetchColorManagementList();
    fetchAcknowledgments();
    fetchInfoSections();
  }, []);

  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  const fetchTypes = async () => {
    const res = await api.get(`/architectural-request-types`);
    setTypes(res.data || []);
  };

  const fetchSubtypes = async () => {
    const res = await api.get(`/architectural-request-subtypes`);
    setSubtypes(res.data || []);
  };

  const fetchColorManagementList = async () => {
    try {
      const res = await api.get("/color-management");
      setAvailableColors(res.data || []);
    } catch (err) {
      console.error("Error loading color list", err);
    }
  };

  const fetchAcknowledgments = async () => {
    try {
      const res = await api.get("/architectural-request-acknowledgments");
      setAcknowledgments(res.data || []);
    } catch (err) {
      console.error("Error loading acknowledgments", err);
      showSnackbar("Error loading acknowledgments", "error");
    }
  };

  const fetchInfoSections = async () => {
    try {
      const res = await api.get("/architectural-request-info-sections");
      setInfoSections(res.data || []);
    } catch (err) {
      console.error("Error loading info sections", err);
    }
  };

  const openInfoDialog = (section = null) => {
    if (section) {
      // find matching preset index
      const presetIdx = COLOR_PRESETS.findIndex(
        (p) => p.bg === section.bg_color && p.border === section.border_color
      );
      setInfoForm({
        title:        section.title,
        content:      section.content,
        content_type: section.content_type,
        bg_color:     section.bg_color,
        border_color: section.border_color,
        color_preset: presetIdx >= 0 ? presetIdx : COLOR_PRESETS.length - 1, // Custom
        sort_order:   section.sort_order,
        is_active:    Boolean(section.is_active),
      });
      setEditingInfo(section);
    } else {
      setInfoForm({
        title: "", content: "", content_type: "paragraph",
        bg_color: "#fffde7", border_color: "#ffe082",
        color_preset: 0, sort_order: 0, is_active: true,
      });
      setEditingInfo(null);
    }
    setInfoDialogOpen(true);
  };

  const handleInfoSubmit = async () => {
    if (!infoForm.title.trim() || !infoForm.content.trim()) {
      showSnackbar("Title and content are required", "error"); return;
    }
    try {
      const payload = {
        title: infoForm.title.trim(),
        content: infoForm.content.trim(),
        content_type: infoForm.content_type,
        bg_color: infoForm.bg_color,
        border_color: infoForm.border_color,
        sort_order: Number(infoForm.sort_order),
        is_active: infoForm.is_active,
      };
      if (editingInfo) {
        await api.put(`/architectural-request-info-sections/${editingInfo.id}`, payload);
        showSnackbar("Section updated");
      } else {
        await api.post("/architectural-request-info-sections", payload);
        showSnackbar("Section created");
      }
      setInfoDialogOpen(false);
      fetchInfoSections();
    } catch (err) {
      showSnackbar("Error saving section", "error");
    }
  };

  const deleteInfoSection = async (id) => {
    if (!window.confirm("Delete this info section?")) return;
    try {
      await api.delete(`/architectural-request-info-sections/${id}`);
      showSnackbar("Section deleted");
      fetchInfoSections();
    } catch {
      showSnackbar("Error deleting section", "error");
    }
  };

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const closeSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const openAddDialog = (type = null) => {
    setFormData(type ? { ...type } : { type: "", id: null });
    setIsEditing(!!type);
    setOpenDialog(true);
  };

  const handleFormSubmit = async () => {
    try {
      if (!formData.type?.trim()) {
        showSnackbar("Type name is required", "warning");
        return;
      }

      if (isEditing) {
        await api.put(`/architectural-request-types/${formData.id}`, formData);
        showSnackbar("Type updated");
      } else {
        await api.post(`/architectural-request-types`, formData);
        showSnackbar("Type added");
      }
      fetchTypes();
      setOpenDialog(false);
    } catch {
      showSnackbar("Error saving type", "error");
    }
  };

  const confirmDeleteType = async (id) => {
    const relatedSubtypes = subtypes.filter((s) => s.type_id === id);
    const typeLabel = types.find((t) => t.id === id)?.type;
    const warning = `Deleting "${typeLabel}" will also remove ${relatedSubtypes.length} subtypes. Continue?`;

    if (window.confirm(warning)) {
      try {
        await api.delete(`/architectural-request-types/${id}`);
        showSnackbar("Type and related subtypes deleted");
        fetchTypes();
        fetchSubtypes();
      } catch {
        showSnackbar("Error deleting type", "error");
      }
    }
  };

  const openSubtypeDialog = (type_id) => {
    setSubtypeForm({
      subtype_id: null,
      type_id,
      subtype_name: "",
      requires_freeform: false,
    });
    setEditingSubtype(null);
    setSubtypeDialog(true);
  };

  const editSubtype = (subtype) => {
    setSubtypeForm({
      ...subtype,
      requires_freeform: subtype.requires_freeform ?? false,
    });
    setEditingSubtype(subtype.subtype_id);
    setSubtypeDialog(true);
  };

  const handleSubtypeSubmit = async () => {
    try {
      if (!subtypeForm.subtype_name?.trim()) {
        showSnackbar("Subtype name is required", "warning");
        return;
      }

      if (editingSubtype) {
        await api.put(
          `/architectural-request-subtypes/${editingSubtype}`,
          subtypeForm
        );
        showSnackbar("Subtype updated");
      } else {
        await api.post(`/architectural-request-subtypes`, subtypeForm);
        showSnackbar("Subtype added");
      }
      fetchSubtypes();
      setSubtypeDialog(false);
    } catch {
      showSnackbar("Error saving subtype", "error");
    }
  };

  const deleteSubtype = async (id) => {
    if (!window.confirm("Delete this subtype?")) return;
    try {
      await api.delete(`/architectural-request-subtypes/${id}`);
      showSnackbar("Subtype deleted");
      fetchSubtypes();
    } catch {
      showSnackbar("Error deleting subtype", "error");
    }
  };

  const openAckDialog = (ack = null) => {
    if (ack) {
      setAckForm({
        id: ack.id,
        bullet_text: ack.bullet_text || "",
        sort_order: ack.sort_order ?? 0,
        is_active: ack.is_active ?? true,
      });
      setEditingAck(ack.id);
    } else {
      setAckForm({
        id: null,
        bullet_text: "",
        sort_order: 0,
        is_active: true,
      });
      setEditingAck(null);
    }

    setAckDialogOpen(true);
  };

  const handleAckSubmit = async () => {
    try {
      if (!ackForm.bullet_text.trim()) {
        showSnackbar("Acknowledgment text is required", "warning");
        return;
      }

      const payload = {
        bullet_text: ackForm.bullet_text.trim(),
        sort_order: Number(ackForm.sort_order) || 0,
        is_active: !!ackForm.is_active,
      };

      if (editingAck) {
        await api.put(
          `/architectural-request-acknowledgments/${editingAck}`,
          payload
        );
        showSnackbar("Acknowledgment updated");
      } else {
        await api.post(`/architectural-request-acknowledgments`, payload);
        showSnackbar("Acknowledgment added");
      }

      fetchAcknowledgments();
      setAckDialogOpen(false);
    } catch (err) {
      console.error("Error saving acknowledgment", err);
      showSnackbar("Error saving acknowledgment", "error");
    }
  };

  const deleteAcknowledgment = async (id) => {
    if (!window.confirm("Delete this acknowledgment?")) return;

    try {
      await api.delete(`/architectural-request-acknowledgments/${id}`);
      showSnackbar("Acknowledgment deleted");
      fetchAcknowledgments();
    } catch (err) {
      console.error("Error deleting acknowledgment", err);
      showSnackbar("Error deleting acknowledgment", "error");
    }
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  const miniActionButtonSx = {
    mt: 1,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: "#2f63ad",
  };

  const inlineItemSx = {
    px: 1.25,
    py: 1,
    borderRadius: "10px",
    backgroundColor: "rgba(47,99,173,0.04)",
    border: "1px solid rgba(47,99,173,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    mb: 1,
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Architectural Request Manager
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Manage architectural request types, subtypes, acknowledgments, and color-related options.
        </Typography>
      </Box>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          minHeight: "auto",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            minHeight: 42,
            color: "#5f6b7a",
            mr: 1,
            px: 2,
          },
          "& .Mui-selected": {
            color: "#2f63ad !important",
            backgroundColor: "rgba(47,99,173,0.08)",
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        <Tab label="Types & Subtypes" />
        <Tab label="Color Management" />
        <Tab label="Billing Setup" />
        <Tab label="Statuses" />
        <Tab label="Form Builder" />
        <Tab label="Status Workflow" />
        <Tab label="Advanced" />
        <Tab label="Manual" />
        <Tab label="Custom Form" />
      </Tabs>

      {tabIndex === 0 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Types & Subtypes
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#6b778c",
                }}
              >
                Organize request types and define subtype options.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog()}
              sx={primaryButtonSx}
            >
              Add Type
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid rgba(47,99,173,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              overflow: "hidden",
              backgroundColor: "rgba(255,255,255,0.96)",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "55%" }}>
                      Subtypes
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {types.length > 0 ? (
                    types.map((type) => {
                      const typeSubtypes = subtypes.filter(
                        (s) => s.type_id === type.id
                      );

                      return (
                        <TableRow key={type.id}>
                          <TableCell sx={{ fontWeight: 600 }}>{type.type}</TableCell>

                          <TableCell sx={{ verticalAlign: "top" }}>
                            {typeSubtypes.length ? (
                              typeSubtypes.map((s) => (
                                <Box key={s.subtype_id} sx={inlineItemSx}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <CategoryIcon
                                      sx={{
                                        fontSize: 18,
                                        color: "#2f63ad",
                                      }}
                                    />
                                    <Typography variant="body2">
                                      {s.subtype_name}
                                    </Typography>

                                    {s.requires_freeform && (
                                      <Chip
                                        label="Freeform"
                                        size="small"
                                        sx={{
                                          fontSize: "0.72rem",
                                          height: 22,
                                          borderRadius: "6px",
                                          backgroundColor: "#2f63ad",
                                          color: "white",
                                        }}
                                      />
                                    )}
                                  </Box>

                                  <Box sx={{ flexShrink: 0 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => editSubtype(s)}
                                    >
                                      <Edit fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteSubtype(s.subtype_id)}
                                    >
                                      <Delete fontSize="inherit" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b778c", mb: 1 }}
                              >
                                No subtypes added.
                              </Typography>
                            )}

                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => openSubtypeDialog(type.id)}
                              sx={miniActionButtonSx}
                            >
                              Add Subtype
                            </Button>
                          </TableCell>

                          <TableCell align="right" sx={{ verticalAlign: "top" }}>
                            <IconButton onClick={() => openAddDialog(type)}>
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => confirmDeleteType(type.id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                        No architectural request types found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {tabIndex === 1 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Color Management
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Manage available colors for architectural requests.
            </Typography>
          </Box>
          <ColorManagement />
        </Box>
      )}

      {tabIndex === 2 && (
        <Box sx={{ mt: 1 }}>
          <ArchitecturalBillingSetupManager />
        </Box>
      )}

      {tabIndex === 3 && (
        <Box sx={{ mt: 1 }}>
          <ModuleStatuses moduleId={3} moduleName="Architectural Request" />
        </Box>
      )}

      {/* ── Form Builder (with Acknowledgments + Info Sections as sub-tabs) ── */}
      {tabIndex === 4 && (
        <Box sx={{ mt: 1 }}>
          {/* Sub-tab bar */}
          <Tabs
            value={formBuilderTab}
            onChange={(_, v) => setFormBuilderTab(v)}
            sx={{
              mb: 3,
              minHeight: "auto",
              borderBottom: "2px solid rgba(47,99,173,0.10)",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 38,
                fontSize: 13,
                color: "#5f6b7a",
                px: 2,
              },
              "& .Mui-selected": { color: "#2f63ad !important" },
              "& .MuiTabs-indicator": { backgroundColor: "#2f63ad", height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            <Tab label="Form Fields" />
            <Tab label="Acknowledgments" />
            <Tab label="Info Sections" />
          </Tabs>

          {/* Form Fields sub-tab */}
          {formBuilderTab === 0 && <ArchitecturalFormBuilder />}

          {/* Acknowledgments sub-tab */}
          {formBuilderTab === 1 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Acknowledgments</Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Manage acknowledgment statements shown before architectural request submission.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAckDialog()} sx={primaryButtonSx}>
                  Add Acknowledgment
                </Button>
              </Box>

              <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid rgba(47,99,173,0.08)", boxShadow: "0 8px 24px rgba(0,0,0,0.06)", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.96)" }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: "10%" }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Acknowledgment Text</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: "15%" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {acknowledgments.length > 0 ? acknowledgments.map((ack) => (
                        <TableRow key={ack.id}>
                          <TableCell>{ack.sort_order}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                              <DescriptionIcon sx={{ fontSize: 18, color: "#2f63ad", mt: 0.4 }} />
                              <Typography variant="body2" sx={{ color: "#223142" }}>{ack.bullet_text}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={ack.is_active ? "Active" : "Inactive"} size="small"
                              sx={{ borderRadius: "6px", backgroundColor: ack.is_active ? "#2f63ad" : "#9aa5b1", color: "white" }} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton onClick={() => openAckDialog(ack)}><Edit /></IconButton>
                            <IconButton color="error" onClick={() => deleteAcknowledgment(ack.id)}><Delete /></IconButton>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ color: "text.secondary" }}>No acknowledgment records found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}

          {/* Info Sections sub-tab */}
          {formBuilderTab === 2 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Info Sections</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage informational boxes shown at the top of the architectural request form.
                    Supports bullet lists or paragraph text. Color the box to match the message tone.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openInfoDialog()}
                  sx={{ background: "#2f63ad", "&:hover": { background: "#1e4c8a" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
                  Add Section
                </Button>
              </Box>

              {infoSections.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: "12px" }}>No info sections yet. Add one above.</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: "14px", boxShadow: "none", border: "1px solid rgba(47,99,173,0.10)" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "rgba(47,99,173,0.05)" }}>
                        <TableCell sx={{ fontWeight: 700, width: 60 }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Content Preview</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 90 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Color</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 90 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {infoSections.map((sec) => (
                        <TableRow key={sec.id} hover>
                          <TableCell>{sec.sort_order}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{sec.title}</TableCell>
                          <TableCell sx={{ color: "#5f6b7a", maxWidth: 340 }}>
                            <Typography variant="body2" noWrap>
                              {sec.content.split("\n")[0].substring(0, 80)}{sec.content.length > 80 ? "…" : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={sec.content_type === "bullets" ? "Bullets" : "Paragraph"} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={`BG: ${sec.bg_color} / Border: ${sec.border_color}`}>
                              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                                <Box sx={{ width: 18, height: 18, borderRadius: "4px", backgroundColor: sec.bg_color, border: `2px solid ${sec.border_color}` }} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip label={sec.is_active ? "Active" : "Inactive"} size="small" color={sec.is_active ? "success" : "default"} />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => openInfoDialog(sec)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteInfoSection(sec.id)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {infoSections.filter(s => s.is_active).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#5f6b7a" }}>
                    LIVE PREVIEW - as members will see it:
                  </Typography>
                  {infoSections.filter(s => s.is_active).map(sec => (
                    <Box key={sec.id} sx={{ mb: 2, p: 2.5, borderRadius: "12px", backgroundColor: sec.bg_color, border: `1px solid ${sec.border_color}` }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{sec.title}</Typography>
                      {sec.content_type === "bullets" ? (
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {sec.content.split("\n").filter(l => l.trim()).map((line, i) => (
                            <li key={i}><Typography variant="body2">{line.trim()}</Typography></li>
                          ))}
                        </ul>
                      ) : (
                        <Typography variant="body2">{sec.content}</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {tabIndex === 5 && (
        <Box sx={{ mt: 1 }}><ARStatusWorkflow /></Box>
      )}
      {tabIndex === 6 && (
        <Box sx={{ mt: 1 }}><ARAdvancedSettings /></Box>
      )}
      {tabIndex === 7 && (
        <Box sx={{ mt: 1 }}><ARManual /></Box>
      )}
      {tabIndex === 8 && (
        <Box sx={{ mt: 1 }}><ARCCustomFormBuilder /></Box>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {isEditing ? "Edit Type" : "Add Type"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Enter the architectural request type name.
          </Typography>

          <TextField
            fullWidth
            autoFocus
            label="Type Name"
            name="type"
            variant="outlined"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" sx={primaryButtonSx}>
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={subtypeDialog}
        onClose={() => setSubtypeDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {editingSubtype ? "Edit Subtype" : "Add Subtype"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Enter the subtype name and configure additional options.
          </Typography>

          <TextField
            fullWidth
            autoFocus
            label="Subtype Name"
            name="subtype_name"
            variant="outlined"
            value={subtypeForm.subtype_name}
            onChange={(e) =>
              setSubtypeForm({ ...subtypeForm, subtype_name: e.target.value })
            }
            sx={fieldSx}
          />

          <Box
            sx={{
              mt: 2,
              px: 1.25,
              py: 1.1,
              borderRadius: "12px",
              backgroundColor: "rgba(47,99,173,0.04)",
              border: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!subtypeForm.requires_freeform}
                  onChange={(e) =>
                    setSubtypeForm({
                      ...subtypeForm,
                      requires_freeform: e.target.checked,
                    })
                  }
                />
              }
              label="Requires Freeform Field"
              sx={{ m: 0 }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setSubtypeDialog(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button onClick={handleSubtypeSubmit} variant="contained" sx={primaryButtonSx}>
            {editingSubtype ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ackDialogOpen}
        onClose={() => setAckDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {editingAck ? "Edit Acknowledgment" : "Add Acknowledgment"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Add or update the acknowledgment statement shown to members before submission.
          </Typography>

          <TextField
            fullWidth
            autoFocus
            multiline
            rows={5}
            label="Acknowledgment Text"
            value={ackForm.bullet_text}
            onChange={(e) =>
              setAckForm({ ...ackForm, bullet_text: e.target.value })
            }
            sx={fieldSx}
          />

          <TextField
            fullWidth
            type="number"
            label="Sort Order"
            value={ackForm.sort_order}
            onChange={(e) =>
              setAckForm({ ...ackForm, sort_order: Number(e.target.value) })
            }
            sx={fieldSx}
          />

          <Box
            sx={{
              mt: 2,
              px: 1.25,
              py: 1.1,
              borderRadius: "12px",
              backgroundColor: "rgba(47,99,173,0.04)",
              border: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!ackForm.is_active}
                  onChange={(e) =>
                    setAckForm({ ...ackForm, is_active: e.target.checked })
                  }
                />
              }
              label="Active"
              sx={{ m: 0 }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setAckDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button onClick={handleAckSubmit} variant="contained" sx={primaryButtonSx}>
            {editingAck ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Info Section Add/Edit Dialog ── */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {editingInfo ? "Edit Info Section" : "Add Info Section"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}>

          <TextField label="Title *" fullWidth value={infoForm.title}
            onChange={e => setInfoForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Before You Get Started" />

          <FormControl fullWidth>
            <InputLabel>Content Type</InputLabel>
            <Select label="Content Type" value={infoForm.content_type}
              onChange={e => setInfoForm(f => ({ ...f, content_type: e.target.value }))}>
              <MenuItem value="paragraph">Paragraph. single block of text</MenuItem>
              <MenuItem value="bullets">Bullet List. one item per line</MenuItem>
            </Select>
          </FormControl>

          <TextField label="Content *" fullWidth multiline minRows={5}
            value={infoForm.content}
            onChange={e => setInfoForm(f => ({ ...f, content: e.target.value }))}
            placeholder={infoForm.content_type === "bullets"
              ? "One bullet item per line:\nColor Photo of front of property\nOfficial Plat Map/Survey..."
              : "Enter the paragraph text to show in this section..."}
            helperText={infoForm.content_type === "bullets" ? "Each line becomes a bullet point." : ""} />

          <FormControl fullWidth>
            <InputLabel>Box Color Theme</InputLabel>
            <Select label="Box Color Theme" value={infoForm.color_preset}
              onChange={e => {
                const idx = e.target.value;
                const preset = COLOR_PRESETS[idx];
                setInfoForm(f => ({
                  ...f,
                  color_preset: idx,
                  bg_color:     preset.bg     || f.bg_color,
                  border_color: preset.border || f.border_color,
                }));
              }}>
              {COLOR_PRESETS.map((p, i) => (
                <MenuItem key={i} value={i}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {p.bg && <Box sx={{ width: 20, height: 20, borderRadius: "4px", backgroundColor: p.bg, border: `2px solid ${p.border}` }} />}
                    {p.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom color inputs. only shown when "Custom" preset selected */}
          {infoForm.color_preset === COLOR_PRESETS.length - 1 && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#5f6b7a", display: "block", mb: 0.5 }}>Background Color</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input type="color" value={infoForm.bg_color}
                    onChange={e => setInfoForm(f => ({ ...f, bg_color: e.target.value }))}
                    style={{ width: 40, height: 40, border: "none", borderRadius: 8, cursor: "pointer" }} />
                  <TextField size="small" value={infoForm.bg_color}
                    onChange={e => setInfoForm(f => ({ ...f, bg_color: e.target.value }))}
                    sx={{ flex: 1 }} placeholder="#fffde7" />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "#5f6b7a", display: "block", mb: 0.5 }}>Border Color</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input type="color" value={infoForm.border_color}
                    onChange={e => setInfoForm(f => ({ ...f, border_color: e.target.value }))}
                    style={{ width: 40, height: 40, border: "none", borderRadius: 8, cursor: "pointer" }} />
                  <TextField size="small" value={infoForm.border_color}
                    onChange={e => setInfoForm(f => ({ ...f, border_color: e.target.value }))}
                    sx={{ flex: 1 }} placeholder="#ffe082" />
                </Box>
              </Box>
            </Box>
          )}

          {/* Live mini-preview */}
          {infoForm.title && (
            <Box sx={{ p: 2, borderRadius: "10px", backgroundColor: infoForm.bg_color, border: `1px solid ${infoForm.border_color}` }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{infoForm.title}</Typography>
              {infoForm.content_type === "bullets" ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {infoForm.content.split("\n").filter(l => l.trim()).slice(0, 3).map((l, i) => (
                    <li key={i}><Typography variant="caption">{l.trim()}</Typography></li>
                  ))}
                  {infoForm.content.split("\n").filter(l => l.trim()).length > 3 && <li><Typography variant="caption" color="text.secondary">…</Typography></li>}
                </ul>
              ) : (
                <Typography variant="caption">{infoForm.content.substring(0, 120)}{infoForm.content.length > 120 ? "…" : ""}</Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField label="Display Order" type="number" size="small" sx={{ width: 130 }}
              value={infoForm.sort_order}
              onChange={e => setInfoForm(f => ({ ...f, sort_order: e.target.value }))} />
            <FormControlLabel
              control={<Switch checked={infoForm.is_active} onChange={e => setInfoForm(f => ({ ...f, is_active: e.target.checked }))} />}
              label="Active" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setInfoDialogOpen(false)} sx={{ color: "#5f6b7a", textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleInfoSubmit}
            sx={{ background: "#2f63ad", "&:hover": { background: "#1e4c8a" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
            {editingInfo ? "Save Changes" : "Add Section"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchitecturalRequestManager;